import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import EmptyState from "@galaxy-io/dls/feedback/EmptyState";
import { InputSize, InputVariant } from "@galaxy-io/dls/inputs/Input";
import PasswordInput from "@galaxy-io/dls/inputs/PasswordInput";
import TextInput from "@galaxy-io/dls/inputs/TextInput";
import Box, { BoxVariant } from "@galaxy-io/dls/layout/Box";
import Menu, { MenuItem } from "@galaxy-io/dls/menu/Menu";
import InfiniteTable from "@galaxy-io/dls/table/InfiniteTable";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { Placement, Side } from "@galaxy-io/dls/theme/enums";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import type { Integration } from "@gtm/contracts";
import { GitBranchIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useRouter, useSearchParams, useUrlState } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DrawerSection } from "@/components/workspace/drawer-content";
import { PageHeader } from "@/components/workspace/page-header";
import { Inline, Stack } from "@/components/workspace/people-styles";
import { ActorLabel, useRecordAudit } from "@/components/workspace/record-audit";
import { SourceIcon } from "@/components/workspace/source-icon";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { TableFooter } from "@/components/workspace/table-footer";
import { TableLayout } from "@/components/workspace/table-layout";
import { Toolbar } from "@/components/workspace/toolbar";
import { useDialogReset } from "@/components/workspace/use-dialog-reset";
import { useRetainedValue } from "@/components/workspace/use-retained-value";
import { AnthropicConnection } from "@/features/integrations/anthropic-connection";
import { RepositoryAvatar } from "@/features/integrations/repository-avatar";
import { IntegrationCards, providerNames, providers } from "@/features/integrations/source-card";
import { WebhookConnection } from "@/features/integrations/webhook-connection";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";
import { ConnectionSurface } from "./connection-surface";
import { IntegrationDrawerContent } from "./integration-drawer-content";
import { PosthogConnection } from "./posthog-connection";
import { RedditConnection } from "./reddit-connection";
import { TrackingActions, useRemoveTrackedItem } from "./remove-tracked-item";

type Repo = {
  id: string;
  fullName: string;
  ownerAvatar: string | null;
  enabled: boolean;
  integrationId: string | null;
};
export function IntegrationSettings({
  connections,
  repositories,
}: {
  connections: Integration[];
  repositories: Repo[];
}) {
  const { params, update } = useUrlState();
  const raw = params.get("integration") || (params.get("flow") === "add" ? "github" : "");
  const retainedKey = useRetainedValue(raw || null) ?? "";
  const selected =
    connections.find((c) => c.id === retainedKey) ||
    connections.find((c) => c.provider === retainedKey);
  const provider = selected?.provider || retainedKey.replace("new-", "");
  if (raw && !(provider in providerNames))
    return (
      <>
        <PageHeader title="Integrations" />
        <IntegrationCards
          connections={connections}
          onManage={(id) =>
            update({ integration: id, integrationTab: null, integrationEdit: null, flow: null })
          }
        />
        <SurfaceDialog
          drawer
          title="Integration not found"
          open
          onClose={() =>
            update({
              integration: null,
              integrationTab: null,
              integrationEdit: null,
              flow: null,
              repoQ: null,
              redditSource: null,
            })
          }
        >
          <Text>This integration is no longer available.</Text>
        </SurfaceDialog>
      </>
    );
  return (
    <IntegrationManager
      key={selected?.id || retainedKey}
      isOpen={!!raw}
      connections={connections}
      selected={selected}
      provider={provider}
      repositories={repositories.filter((r) => r.integrationId === selected?.id)}
    />
  );
}
function IntegrationManager({
  isOpen,
  connections,
  selected,
  provider,
  repositories,
}: {
  isOpen: boolean;
  connections: Integration[];
  selected?: Integration;
  provider: string;
  repositories: Repo[];
}) {
  const [formError, setFormError] = useState<string | undefined>();
  const [adding, setAdding] = useState(false);
  const removal = useRemoveTrackedItem<Repo>(
    "github",
    "repository",
    "github-repositories",
    (row) => row.fullName,
  );
  const connected = !!selected?.enabled;
  const slack = selected?.provider === "slack" ? selected : null;
  const creating = !selected && ["github", "slack"].includes(provider);
  const router = useRouter();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  function notify(header: string, subheader: string) {
    toast({
      variant: ToastVariant.SUCCESS,
      header,
      description: subheader,
      ...toastDefaults(ToastVariant.SUCCESS),
    });
  }
  const params = useSearchParams();
  const { update } = useUrlState();
  const view = provider;
  const flow = params.get("flow") || "";
  function setView(value: string) {
    update({
      integration: value || null,
      integrationTab: null,
      integrationEdit: null,
      flow: null,
      repoQ: null,
      redditSource: null,
    });
  }
  function setFlow(value: string) {
    update({ flow: value || null });
  }
  const repositorySearch = params.get("repoQ") || "";
  const setRepositorySearch = (value: string) => update({ repoQ: value || null }, true);
  const visibleRepositories = repositories.filter((repository) =>
    repository.fullName.toLowerCase().includes(repositorySearch.trim().toLowerCase()),
  );
  const repositoryAudit = useRecordAudit(
    "source",
    visibleRepositories.map((r) => r.id),
  );
  const [connectionName, setConnectionName] = useState(
    selected?.name || (provider === "github" ? "GitHub" : "Slack"),
  );
  const [webhook, setWebhook] = useState(""),
    [channel, setChannel] = useState(slack?.account || ""),
    [token, setToken] = useState(""),
    [fullName, setFullName] = useState(""),
    [busy, setBusy] = useState(false);
  useDialogReset(isOpen && (!selected || params.get("integrationEdit") === "1"), () => {
    setFormError(undefined);
    setConnectionName(selected?.name || (provider === "github" ? "GitHub" : "Slack"));
    setChannel(slack?.account || "");
    setWebhook("");
    setToken("");
    setBusy(false);
  });
  useDialogReset(isOpen && flow === "add", () => setFullName(""));
  async function action<T>(request: () => Promise<T>) {
    setFormError(undefined);
    setBusy(true);
    try {
      const result = await request();
      await queryClient.invalidateQueries({ queryKey: ["integrations"] });
      router.refresh();
      return result;
    } catch (e) {
      setFormError((e as Error).message);
      return null;
    } finally {
      setBusy(false);
    }
  }
  function open(name: string) {
    setFlow(name);
  }
  return (
    <>
      {removal.dialog}
      <PageHeader
        title="Integrations"
        actions={
          <Menu
            isOpen={adding}
            onOpenChange={setAdding}
            placement={Placement.BOTTOM_END}
            trigger={<Button label="Connect integration" icon={PlusIcon} />}
          >
            {providers.map((provider) => (
              <MenuItem
                key={provider}
                label={providerNames[provider]}
                leading={
                  <Box aria-hidden="true">
                    <SourceIcon source={provider} size={18} />
                  </Box>
                }
                onSelect={() => {
                  setAdding(false);
                  setView(`new-${provider}`);
                }}
              />
            ))}
          </Menu>
        }
      />
      <IntegrationCards connections={connections} onManage={setView} />
      {view === "reddit" && (
        <RedditConnection
          open={isOpen}
          key={selected?.id || "new-reddit"}
          selected={selected}
          onClose={() => setView("")}
          onSaved={(id) => (selected ? update({ integrationEdit: null }) : setView(id))}
        />
      )}
      {view === "posthog" && (
        <PosthogConnection
          open={isOpen}
          key={selected?.id || "new-posthog"}
          selected={selected}
          onClose={() => setView("")}
          onSaved={(id) => (selected ? update({ integrationEdit: null }) : setView(id))}
        />
      )}
      {view === "anthropic" && (
        <AnthropicConnection
          open={isOpen}
          key={selected?.id || "new-anthropic"}
          selected={selected}
          onClose={() => setView("")}
          onSaved={(id) => (selected ? update({ integrationEdit: null }) : setView(id))}
        />
      )}
      {view === "webhook" && (
        <WebhookConnection
          open={isOpen}
          key={selected?.id || "new-webhook"}
          selected={selected}
          onClose={() => setView("")}
          onSaved={(id) => (selected ? update({ integrationEdit: null }) : setView(id))}
        />
      )}
      <ConnectionSurface
        open={isOpen && view === "github"}
        title={selected?.name || "Connect GitHub"}
        headerLeading={<SourceIcon source="github" size={36} />}
        flush
        selected={selected}
        details={
          selected && (
            <DrawerSection header="Repositories" icon={GitBranchIcon} defaultIsOpen isFlush>
              <Toolbar
                trailingActions={
                  <Button
                    label="Add repository"
                    variant={ButtonVariant.SECONDARY}
                    icon={PlusIcon}
                    onClick={() => open("add")}
                    isDisabled={!connected}
                    size={ButtonSize.SMALL}
                  />
                }
              >
                <Input
                  aria-label="Search repositories"
                  placeholder="Search repositories…"
                  value={repositorySearch}
                  onChange={(event) => setRepositorySearch(event)}
                  type="search"
                  size={InputSize.SMALL}
                  label="Search repositories"
                  hideLabel
                />
              </Toolbar>
              <TableLayout
                fillHeight={false}
                footer={
                  <TableFooter label="Tracked repositories" total={visibleRepositories.length} />
                }
              >
                <Box variant={BoxVariant.BASE}>
                  <InfiniteTable
                    ariaLabel="Tracked repositories"
                    canResizeColumns
                    data={visibleRepositories}
                    getRowId={(repository) => repository.id}
                    columns={[
                      {
                        id: "repository",
                        header: "Repository",
                        accessor: (repository) => repository.fullName,

                        cell: ({ row }) => (
                          <Link
                            href={`https://github.com/${row.fullName}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Inline>
                              <RepositoryAvatar
                                owner={row.fullName.split("/")[0]}
                                src={row.ownerAvatar}
                                size={24}
                              />
                              <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM}>
                                {row.fullName}
                              </Text>
                            </Inline>
                          </Link>
                        ),
                        isRowHeader: true,
                      },
                      {
                        id: "createdBy",
                        header: "Created by",
                        width: 200,

                        cell: ({ row }) => (
                          <ActorLabel
                            actor={repositoryAudit.data?.find((a) => a.id === row.id)?.createdBy}
                          />
                        ),
                      },
                      {
                        id: "actions",
                        header: "",
                        // One 28px icon button plus the cell padding; the column
                        // hugs the control instead of stranding it.
                        width: 60,
                        minWidth: 60,
                        maxWidth: 60,
                        canResize: false,
                        pin: Side.RIGHT,
                        align: "right" as const,

                        cell: ({ row }) => (
                          <TrackingActions>
                            <Button
                              icon={TrashIcon}
                              ariaLabel={`Remove ${row.fullName}`}
                              variant={ButtonVariant.TERTIARY}
                              isDisabled={removal.busy}
                              onClick={() => removal.request(row.id, row.fullName)}
                              tooltip={`Remove ${row.fullName}`}
                            />
                          </TrackingActions>
                        ),
                      },
                    ]}
                    emptyState={
                      <EmptyState
                        header={
                          repositorySearch ? "No matching repositories" : "No repositories yet"
                        }
                        description={
                          repositorySearch
                            ? "Try another search."
                            : "Track a source to start collecting activity."
                        }
                      />
                    }
                  />
                </Box>
              </TableLayout>
            </DrawerSection>
          )
        }
        onClose={() => setView("")}
        dirty={!!token || connectionName !== (selected?.name || "GitHub")}
        busy={busy}
        footer={
          <Button
            label={selected ? "Save connection" : "Connect GitHub"}
            isDisabled={busy || (creating && !token) || !connectionName.trim()}
            isLoading={busy}
            onClick={async () => {
              const result = await action(() =>
                creating
                  ? unwrap(
                      apiClient().POST("/integrations/github/connections", {
                        body: { name: connectionName, token },
                      }),
                    )
                  : unwrap(
                      apiClient().PUT("/integrations/github/connections/{id}", {
                        params: { path: { id: selected!.id } },
                        body: { name: connectionName, token },
                      }),
                    ),
              );
              if (result) {
                setToken("");
                notify("GitHub connection saved", "Connection settings saved.");
                if (creating) setView(result.id);
                else update({ integrationEdit: null });
              }
            }}
          />
        }
      >
        {formError && <Alert variant={AlertVariant.ERROR}>{formError}</Alert>}
        {view === "github" && (
          <IntegrationDrawerContent>
            <Stack>
              <TextInput
                label="Integration name"
                ref={(input) => input?.setAttribute("aria-label", "Integration name")}
                value={connectionName}
                onChange={setConnectionName}
                isDisabled={busy}
                variant={InputVariant.BASE}
                fillWidth
              />
              <PasswordInput
                label="GitHub access token"
                ref={(input) => input?.setAttribute("aria-label", "GitHub access token")}
                value={token}
                onChange={setToken}
                isDisabled={busy}
                variant={InputVariant.BASE}
                fillWidth
                placeholder={
                  selected ? "Leave blank to keep the saved token" : "Paste a personal access token"
                }
              />
              {connected && (
                <Inline>
                  <Button
                    label="Test connection"
                    variant={ButtonVariant.SECONDARY}
                    isDisabled={busy}
                    onClick={async () => {
                      const result = await action(() =>
                        unwrap(
                          apiClient().POST("/integrations/github/connections/{id}/test", {
                            params: { path: { id: selected!.id } },
                          }),
                        ),
                      );
                      if (result) notify("Token valid", `Authenticated as ${result.account}.`);
                    }}
                  />
                </Inline>
              )}
            </Stack>
          </IntegrationDrawerContent>
        )}
      </ConnectionSurface>
      <ConnectionSurface
        open={isOpen && view === "slack"}
        title={selected?.name || "Connect Slack"}
        headerLeading={<SourceIcon source="slack" size={36} />}
        flush
        selected={selected}
        onClose={() => setView("")}
        dirty={
          !!webhook ||
          channel !== (slack?.account || "") ||
          connectionName !== (selected?.name || "Slack")
        }
        busy={busy}
        footer={
          <Button
            label={slack ? "Save connection" : "Connect Slack"}
            isDisabled={busy || (creating && !webhook) || !channel.trim() || !connectionName.trim()}
            isLoading={busy}
            onClick={async () => {
              const result = await action(() =>
                creating
                  ? unwrap(
                      apiClient().POST("/integrations/slack/connections", {
                        body: { name: connectionName, webhook, channel },
                      }),
                    )
                  : unwrap(
                      apiClient().PUT("/integrations/slack/connections/{id}", {
                        params: { path: { id: selected!.id } },
                        body: { name: connectionName, webhook, channel },
                      }),
                    ),
              );
              if (result) {
                setWebhook("");
                notify("Slack configured", "Records can be sent to this workspace.");
                if (creating) setView(result.id);
                else update({ integrationEdit: null });
              }
            }}
          />
        }
      >
        {formError && <Alert variant={AlertVariant.ERROR}>{formError}</Alert>}
        <IntegrationDrawerContent>
          <Stack>
            <TextInput
              ref={(input) => input?.setAttribute("aria-label", "Integration name")}
              label="Integration name"
              value={connectionName}
              onChange={setConnectionName}
              isDisabled={busy}
              variant={InputVariant.BASE}
              fillWidth
            />
            <TextInput
              ref={(input) => input?.setAttribute("aria-label", "Channel")}
              label="Channel"
              placeholder="#gtm"
              value={channel}
              onChange={setChannel}
              isDisabled={busy}
              variant={InputVariant.BASE}
              fillWidth
            />
            <PasswordInput
              ref={(input) => input?.setAttribute("aria-label", "Incoming webhook URL")}
              label="Incoming webhook URL"
              placeholder={
                slack ? "Paste a new webhook to update" : "https://hooks.slack.com/services/…"
              }
              value={webhook}
              onChange={setWebhook}
              isDisabled={busy}
              variant={InputVariant.BASE}
              fillWidth
            />
            {slack && (
              <Inline>
                <Button
                  label={slack.isDefault ? "Receives processed items" : "Use for processed items"}
                  variant={ButtonVariant.SECONDARY}
                  isDisabled={busy || slack.isDefault || !slack.enabled}
                  onClick={async () => {
                    const result = await action(() =>
                      unwrap(
                        apiClient().PUT("/integrations/slack/connections/{id}", {
                          params: { path: { id: slack.id } },
                          body: {
                            name: connectionName,
                            channel,
                            isDefault: true,
                          },
                        }),
                      ),
                    );
                    if (result)
                      notify(
                        "Processing destination updated",
                        "Processed items go to this Slack connection.",
                      );
                  }}
                />
                <Button
                  label="Send test message"
                  isDisabled={busy || !slack.enabled}
                  variant={ButtonVariant.SECONDARY}
                  onClick={async () => {
                    const result = await action(() =>
                      unwrap(
                        apiClient().POST("/integrations/slack/connections/{id}/test", {
                          params: { path: { id: slack.id } },
                        }),
                      ),
                    );
                    if (result)
                      notify("Test message sent", "Check the channel to confirm it arrived.");
                  }}
                />
              </Inline>
            )}
          </Stack>
        </IntegrationDrawerContent>
      </ConnectionSurface>

      <SurfaceDialog
        open={isOpen && view === "github" && !!selected && flow === "add"}
        title="Add repository"
        onClose={() => setFlow("")}
        dirty={!!fullName}
        busy={busy}
        footer={
          <>
            <Button
              label="Cancel"
              variant={ButtonVariant.SECONDARY}
              isDisabled={busy}
              onClick={() => setFlow("")}
            />
            <Button
              label="Track repository"
              isDisabled={busy || !fullName.trim()}
              isLoading={busy}
              onClick={async () => {
                const result = await action(() =>
                  unwrap(
                    apiClient().POST("/integrations/github/repositories", {
                      body: { fullName, integrationId: selected!.id },
                    }),
                  ),
                );
                if (result) {
                  setFullName("");
                  setFlow("");
                  notify("Repository added", "Run collection from Workflows to start ingesting.");
                }
              }}
            />
          </>
        }
      >
        {formError && <Alert variant={AlertVariant.ERROR}>{formError}</Alert>}
        <Input
          aria-label="Repository owner/name"
          placeholder="owner/repository"
          value={fullName}
          disabled={busy}
          onChange={(e) => setFullName(e)}
          label="Repository owner/name"
        />
        <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
          Use owner/repository, including public repositories outside your organization.
        </Text>
      </SurfaceDialog>
    </>
  );
}
